import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { CHANNEL, colorPageSlugs } from '@boss/constants/b2b-b2c';
import { contentfulContext } from '@boss/contentful-client';
import { getEntryByTag } from '@boss/contentful-client/b2b-b2c';
import { color as colorService, content as contentService } from '@boss/services';
import { Environment, SectionFields } from '@boss/types/b2b-b2c';

import { SEO } from '../../components';
import { IS_BUILD, PageTypes } from '../../constants';
import { ColorOverviewPage } from '../../containers';
import { useColorGroup } from '../../hooks';
import Layout from '../../layout/default';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket, mapNextToContentfulMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';

export const getStaticProps = async ({
  preview,
  locale,
}: GetStaticPropsContext<{ slug: string[]; locale: string }>) => {
  if (!locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
  const market = mapNextToContentfulMarket(locale);

  if (!contentfulLocaleMarket || !market) {
    return {
      notFound: true,
    };
  }

  // SET GLOBAL MARKET CONTEXT
  contentfulContext.market = market;

  const [navigation, overviewPage, colorGroups, generalContentData, colorToolSection] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    contentService.getOverviewPage({
      locale: contentfulLocaleMarket,
      channel: CHANNEL,
      type: 'Color overview',
      batchMode: IS_BUILD,
      preview: !!preview,
      market,
    }),
    colorService.getColorGroups(contentfulLocaleMarket),
    colorService.getColorOverviewPageData(contentfulLocaleMarket, CHANNEL),
    getEntryByTag<SectionFields>('displayColorPageColorToolOverview', contentfulLocaleMarket),
  ]);

  return {
    props: {
      pageType: PageTypes.COLOR_OVERVIEW,
      locale,
      preview: !!preview,
      pageData: { colorGroups, generalContent: generalContentData, overviewPage },
      navigation,
      colorToolSection,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      ...(await getServerSideTranslations(locale, ['common', 'color', 'product', 'forms', 'colorTool'])),
    },
  };
};

export const ColorOverview = ({
  pageData,
  navigation,
  locale,
  preview,
  environment,
  colorToolSection,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation();
  const { normalizeColorGroupNames } = useColorGroup();
  const pageTitle = t('colorPicker', { ns: 'color' });
  const pageDescription = pageTitle; // TODO: Update with final value once this is known
  const crumbles = [{ href: colorPageSlugs[locale], label: pageTitle }];
  const { colorGroups, generalContent, overviewPage } = pageData;
  const enrichedGroups = normalizeColorGroupNames(colorGroups);

  return (
    <SEO
      breadcrumbs={crumbles}
      description={overviewPage?.metaDescription ?? pageDescription}
      imageSrc={overviewPage?.ogImage?.image?.file?.url}
      localizedSlugs={colorPageSlugs}
      navigation={navigation}
      noFollow={overviewPage?.noFollow}
      noIndex={overviewPage?.noIndex}
      title={overviewPage?.pageTitle ?? pageTitle}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={colorPageSlugs}
        navigation={navigation}
        preview={preview}
      >
        <ColorOverviewPage
          cmsData={overviewPage}
          colorGroups={enrichedGroups}
          colorToolSection={colorToolSection}
          generalContent={generalContent}
        />
      </Layout>
    </SEO>
  );
};

export default ColorOverview;
